import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { Spinner } from '@bibliocommons/base-icons';

import {
  deleteOne as deleteCategoryConfig,
  fetchAllForTopLevelCategory as fetchCategoryConfigIndex
} from 'app/api/admin/browse/categoryConfigsApi';
import ErrorPage from 'app/components/ErrorPage';
import { TOP_LEVEL_CATEGORY_ID, TOP_LEVEL_CATEGORY_TITLE_BY_ID } from 'app/constants/AdminBrowseConstants';
import { MESSAGE_ACTION_GENERIC_ERROR } from 'app/constants/admin/browse';
import { BROWSE_HOME_PAGE_PATH } from 'app/constants/BrowseConstants';
import { HTTP_STATUS_CODE } from 'app/constants/ErrorConstants';
import { makeSuccessMessage } from 'app/helpers/admin/browse';
import { hasAdminPermissions, isTopLevelCategoryId } from 'app/helpers/admin/browse/categories';
import useLocaleCodes from 'app/hooks/useLocalCodes';
import useApiClient from 'app/hooks/useApiClient';
import { selectCurrentUser } from 'app/selectors/AuthSelector';
import CategoryConfigsList from './CategoryConfigsList';
import AdminBrowseCategoriesListPage from './AdminBrowseCategoriesListPage';

const AdminBrowseCategoriesListPageContainer = ({ basePageRoute, topLevelCategoryId }) => {
  const history = useHistory();

  const { availableLocaleCodes, mapLocaleCodeToLabel } = useLocaleCodes();

  const currentUser = useSelector(selectCurrentUser);

  const apiClient = useApiClient();
  const dispatch = useDispatch();

  const [state, setState] = useState({
    categoryConfigByLocaleCode: {},
    error: '',
    isFetching: false,
    isSaving: false
  });

  const { categoryConfigByLocaleCode, error, isFetching, isSaving } = state;

  useEffect(() => {
    setState(prevState => ({ ...prevState, error: '', isFetching: true }));
    fetchCategoryConfigIndex(apiClient, topLevelCategoryId)
      .then(response => {
        setState(prevState => {
          return {
            ...prevState,
            categoryConfigByLocaleCode: response.categoryConfigByLocaleCode ?? {}
          };
        });
      })
      .catch(err => {
        dispatch(MESSAGE_ACTION_GENERIC_ERROR);
        /* eslint-disable-next-line no-console */
        console.error(err.message);
      })
      .finally(() => {
        setState(prevState => {
          return {
            ...prevState,
            isFetching: false
          };
        });
      });
  }, [apiClient, dispatch, topLevelCategoryId]);

  const mapTopLevelCategoryIdToLabel = nextTopLevelCategoryId => {
    return TOP_LEVEL_CATEGORY_TITLE_BY_ID[nextTopLevelCategoryId];
  };

  const onLocaleCodeDelete = useCallback(
    localeCodeToDelete => {
      const categoryConfigToDelete = categoryConfigByLocaleCode[localeCodeToDelete];
      if (!categoryConfigToDelete) {
        throw new Error('Category config not found');
      }

      setState(prevState => ({ ...prevState, isSaving: true }));
      deleteCategoryConfig(apiClient, topLevelCategoryId, localeCodeToDelete, categoryConfigToDelete.categoryConfigId)
        .then(() => {
          const label = mapLocaleCodeToLabel(localeCodeToDelete);
          dispatch(makeSuccessMessage(`${label} categories deleted`));

          setState(prevState => {
            const newCategoryConfigByLocaleCode = {
              ...prevState.categoryConfigByLocaleCode
            };

            delete newCategoryConfigByLocaleCode[localeCodeToDelete];

            return {
              ...prevState,
              categoryConfigByLocaleCode: newCategoryConfigByLocaleCode
            };
          });
        })
        .catch(err => {
          dispatch(MESSAGE_ACTION_GENERIC_ERROR);
          /* eslint-disable-next-line no-console */
          console.error(err.message);
        })
        .finally(() => {
          setState(prevState => {
            return {
              ...prevState,
              isSaving: false
            };
          });
        });
    },
    [apiClient, categoryConfigByLocaleCode, dispatch, mapLocaleCodeToLabel, topLevelCategoryId]
  );

  const onCreateCategoryConfig = useCallback(
    localeCode => {
      history.push(`${basePageRoute}/${topLevelCategoryId}/${localeCode}/create`);
    },
    [basePageRoute, history, topLevelCategoryId]
  );

  const onSelectCategoryConfig = useCallback(
    categoryConfig => {
      const { categoryConfigId, localeCode } = categoryConfig;
      history.push(`${basePageRoute}/${topLevelCategoryId}/${localeCode}/${categoryConfigId}`);
    },
    [basePageRoute, history, topLevelCategoryId]
  );

  const onViewLocaleCode = useCallback(
    localeCode => {
      window.open(`${BROWSE_HOME_PAGE_PATH}/${topLevelCategoryId}?locale=${encodeURIComponent(localeCode)}`, '_blank');
    },
    [topLevelCategoryId]
  );

  const renderPanelBody = useCallback(() => {
    if (isFetching) {
      return <Spinner />;
    }

    if (error) {
      return <p style={{ color: 'red' }}>{error}</p>;
    }

    return (
      <CategoryConfigsList
        categoryConfigByLocaleCode={categoryConfigByLocaleCode}
        isSaving={isSaving}
        localeCodes={availableLocaleCodes}
        mapLocaleCodeToLabel={mapLocaleCodeToLabel}
        onCreateCategoryConfig={onCreateCategoryConfig}
        onLocaleCodeDelete={onLocaleCodeDelete}
        onSelectCategoryConfig={onSelectCategoryConfig}
        onViewLocaleCode={onViewLocaleCode}
        topLevelCategoryLabel={mapTopLevelCategoryIdToLabel(topLevelCategoryId)}
      />
    );
  }, [
    availableLocaleCodes,
    categoryConfigByLocaleCode,
    error,
    isFetching,
    isSaving,
    mapLocaleCodeToLabel,
    onCreateCategoryConfig,
    onLocaleCodeDelete,
    onSelectCategoryConfig,
    onViewLocaleCode,
    topLevelCategoryId
  ]);

  const onTabSelected = useCallback(
    selectedTab => {
      const { id: newTopLevelCategoryId } = selectedTab;

      history.push(`${basePageRoute}/${newTopLevelCategoryId}`);
    },
    [basePageRoute, history]
  );

  const tabOptions = useMemo(() => {
    return [
      {
        id: TOP_LEVEL_CATEGORY_ID.COLLECTION,
        label: mapTopLevelCategoryIdToLabel(TOP_LEVEL_CATEGORY_ID.COLLECTION),
        renderPanelBody
      },
      {
        id: TOP_LEVEL_CATEGORY_ID.FORMAT,
        label: mapTopLevelCategoryIdToLabel(TOP_LEVEL_CATEGORY_ID.FORMAT),
        renderPanelBody
      },
      {
        id: TOP_LEVEL_CATEGORY_ID.GENRE,
        label: mapTopLevelCategoryIdToLabel(TOP_LEVEL_CATEGORY_ID.GENRE),
        renderPanelBody
      }
    ];
  }, [renderPanelBody]);

  const canViewPage = hasAdminPermissions(currentUser);
  if (!canViewPage) {
    return <ErrorPage httpStatusCode={HTTP_STATUS_CODE.FORBIDDEN} />;
  }

  const isValidTopLevelCategoryId = isTopLevelCategoryId(topLevelCategoryId);
  if (!isValidTopLevelCategoryId) {
    return <ErrorPage httpStatusCode={HTTP_STATUS_CODE.NOT_FOUND} />;
  }

  return (
    <AdminBrowseCategoriesListPage
      onTabSelected={onTabSelected}
      tabOptions={tabOptions}
      topLevelCategoryId={topLevelCategoryId}
    />
  );
};

AdminBrowseCategoriesListPageContainer.propTypes = {
  basePageRoute: PropTypes.string.isRequired,
  topLevelCategoryId: PropTypes.oneOf(Object.values(TOP_LEVEL_CATEGORY_ID)).isRequired
};

export default AdminBrowseCategoriesListPageContainer;
